/**
 * Utility functions for date and time handling
 */
import { fromZonedTime, toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';

/**
 * Get the user's browser timezone (e.g., 'America/Los_Angeles')
 * @returns {string} IANA timezone string
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'UTC';
  }
};

/** Admin console: when a meeting has no IANA timezone, show instants in UTC (not the viewer's browser zone). */
export const CONSOLE_RUN_DISPLAY_TZ_FALLBACK = 'UTC';

/** Mon–Sun labels matching console RunsView grid / sorting. */
const CONSOLE_RUN_DAYS_MON_FIRST = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * IANA timezone for displaying a run in the admin console.
 * @param {{ timezone?: string | null } | null | undefined} meetingOrEvent
 * @returns {string}
 */
export function getConsoleRunDisplayTimezone(meetingOrEvent) {
  const raw = meetingOrEvent?.timezone;
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim();
  }
  return CONSOLE_RUN_DISPLAY_TZ_FALLBACK;
}

/**
 * True when the meeting/event has no IANA timezone stored (whitespace-only counts as unset).
 * Used for admin console UTC display and unset notes. Explicit "UTC" is treated as set.
 * @param {{ timezone?: string | null } | null | undefined} meetingOrEvent
 * @returns {boolean}
 */
export function isConsoleRunTimezoneUnset(meetingOrEvent) {
  const raw = meetingOrEvent?.timezone;
  return raw == null || String(raw).trim() === '';
}

/** Admin console: run list / table row when {@link isConsoleRunTimezoneUnset}. */
export const CONSOLE_RUN_TIMEZONE_UNSET_NOTE_LIST =
  'No run timezone set — time shown in UTC.';

/** Admin console: run detail when {@link isConsoleRunTimezoneUnset}. */
export const CONSOLE_RUN_TIMEZONE_UNSET_NOTE_DETAIL =
  'Not set — day and time below are shown in UTC. Set a timezone when editing this run.';

/**
 * Parse scheduled_date from API to a Date. Naive ISO without offset is treated as UTC (same rule as legacy formatTimeInTimezone).
 * @param {Date|string|number|null|undefined} date
 * @returns {Date|null}
 */
export function parseScheduledInstantForDisplay(date) {
  if (date == null || date === '') return null;
  try {
    if (date instanceof Date) {
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof date === 'string') {
      if (!date.includes('Z') && !date.includes('+') && !date.includes('-', 10)) {
        const d = new Date(`${date}Z`);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(date);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function resolveDisplayTimezoneIana(timezone) {
  if (timezone != null && String(timezone).trim() !== '') {
    return String(timezone).trim();
  }
  return CONSOLE_RUN_DISPLAY_TZ_FALLBACK;
}

/**
 * Map date-fns ISO weekday (1=Mon … 7=Sun) to RunsView day label.
 * @param {number} isoD
 * @returns {string | null}
 */
function isoWeekdayToConsoleRunDay(isoD) {
  if (!Number.isFinite(isoD) || isoD < 1 || isoD > 7) return null;
  return CONSOLE_RUN_DAYS_MON_FIRST[isoD === 7 ? 6 : isoD - 1];
}

/**
 * Day (Mon–Sun), 12h time string, and am/pm slot from a UTC instant in a specific IANA zone.
 * Matches legacy RunsView formatting (omit ":00" when minutes are zero).
 * @param {Date|string|null|undefined} scheduledDate
 * @param {string|null|undefined} timezone - IANA; missing uses {@link CONSOLE_RUN_DISPLAY_TZ_FALLBACK}
 * @returns {{ day: string | null, time: string | null, slot: 'am' | 'pm' | null }}
 */
export function getRunDayTimeSlotInTimezone(scheduledDate, timezone) {
  const tz = timezone && String(timezone).trim() !== '' ? String(timezone).trim() : CONSOLE_RUN_DISPLAY_TZ_FALLBACK;
  if (scheduledDate == null) {
    return { day: null, time: null, slot: null };
  }
  try {
    const d = parseScheduledInstantForDisplay(scheduledDate);
    if (!d) {
      return { day: null, time: null, slot: null };
    }
    const isoD = parseInt(formatInTimeZone(d, tz, 'i'), 10);
    const day = isoWeekdayToConsoleRunDay(isoD);
    const hours = parseInt(formatInTimeZone(d, tz, 'H'), 10);
    const mins = parseInt(formatInTimeZone(d, tz, 'm'), 10);
    if (!Number.isFinite(hours) || !Number.isFinite(mins) || !day) {
      return { day: null, time: null, slot: null };
    }
    const am = hours < 12;
    const slot = am ? 'am' : 'pm';
    const h12 = hours % 12 || 12;
    const time =
      mins > 0
        ? `${h12}:${String(mins).padStart(2, '0')} ${am ? 'AM' : 'PM'}`
        : `${h12} ${am ? 'AM' : 'PM'}`;
    return { day, time, slot };
  } catch (e) {
    return { day: null, time: null, slot: null };
  }
}

/**
 * Format a date-only value (ISO string or Date) for display in a given IANA timezone.
 * @param {Date|string|null|undefined} value
 * @param {string|null|undefined} timezone
 * @returns {string}
 */
export function formatDateOnlyInTimezone(value, timezone) {
  const tz = getConsoleRunDisplayTimezone({ timezone });
  if (value == null || value === '') return '';
  try {
    const d = parseScheduledInstantForDisplay(value);
    if (!d) return '';
    return formatInTimeZone(d, tz, 'MMM d, yyyy');
  } catch {
    return '';
  }
}

/**
 * Convert a local Date object to UTC ISO string
 * The Date object is already in the browser's local timezone, so we just convert to UTC
 * @param {Date} localDate - Date object representing local time (user selected 2:30 PM in browser timezone)
 * @param {string} timezone - IANA timezone string (should match browser timezone, used for validation)
 * @returns {string} ISO datetime string in UTC
 */
export const convertLocalToUTC = (localDate, timezone) => {
  if (!localDate || !(localDate instanceof Date)) {
    throw new Error('Date must be a valid Date object');
  }
  
  // The Date object is already in the browser's local timezone
  // JavaScript's toISOString() automatically converts from local time to UTC
  // So we can just use toISOString() directly
  // The timezone parameter is kept for API compatibility but the Date object already has the correct timezone
  try {
    return localDate.toISOString();
  } catch (e) {
    console.error('Error converting local time to UTC:', e);
    return localDate.toISOString();
  }
};

/**
 * Convert a UTC datetime string to a Date object in the meeting's timezone
 * @param {string} utcString - ISO datetime string in UTC
 * @param {string} meetingTimezone - IANA timezone string
 * @returns {Date} Date object representing the time in the meeting's timezone
 */
export const convertUTCToMeetingTimezone = (utcString, meetingTimezone) => {
  if (!utcString) return null;
  if (!meetingTimezone) {
    return new Date(utcString);
  }
  
  try {
    const utcDate = new Date(utcString);
    if (isNaN(utcDate.getTime())) {
      return null;
    }
    const meetingDate = toZonedTime(utcDate, meetingTimezone);
    return meetingDate;
  } catch (e) {
    console.error('Error converting UTC to meeting timezone:', e);
    return new Date(utcString);
  }
};

/**
 * Format a datetime in a specific timezone for display
 * @param {Date|string} date - Date object or ISO string
 * @param {string} timezone - IANA timezone string
 * @param {string} formatStr - Format string (default: 'h:mm a')
 * @returns {string} Formatted time string
 */
export const formatTimeInTimezone = (date, timezone, formatStr = 'h:mm a') => {
  if (!date) return '';

  try {
    const d = parseScheduledInstantForDisplay(date);
    if (!d) return '';

    if (!timezone) {
      return format(d, formatStr);
    }
    return formatInTimeZone(d, timezone, formatStr);
  } catch (e) {
    const d = parseScheduledInstantForDisplay(date);
    return d ? format(d, formatStr) : '';
  }
};

/**
 * Format a date with time-relative labels ("Today", "Tonight", "Tomorrow") or day of week.
 * Matches mobile RunScreen display for day/date.
 * @param {Date|string} dateString - UTC date object or ISO string
 * @param {string} meetingTimezone - IANA timezone string for meeting (optional)
 * @param {Object} options - { includeTime: boolean }
 * @returns {string} e.g. "Today, Feb 14" or "Tomorrow, Feb 15" or "Sat, Feb 15"
 */
export const formatDateWithTimeRelative = (dateString, meetingTimezone = null, options = {}) => {
  const { includeTime = false } = options;
  if (!dateString) return includeTime ? '' : 'TBD';

  try {
    const utcDate = parseScheduledInstantForDisplay(dateString);
    if (!utcDate) return includeTime ? '' : 'TBD';

    const userTimezone = getUserTimezone();
    const userDate = toZonedTime(utcDate, userTimezone);
    const nowUTC = new Date();
    const userNow = toZonedTime(nowUTC, userTimezone);
    const tomorrowUTC = new Date(nowUTC);
    tomorrowUTC.setUTCDate(tomorrowUTC.getUTCDate() + 1);
    const userTomorrow = toZonedTime(tomorrowUTC, userTimezone);

    const isToday =
      userDate.getFullYear() === userNow.getFullYear() &&
      userDate.getMonth() === userNow.getMonth() &&
      userDate.getDate() === userNow.getDate();
    const isTomorrow =
      userDate.getFullYear() === userTomorrow.getFullYear() &&
      userDate.getMonth() === userTomorrow.getMonth() &&
      userDate.getDate() === userTomorrow.getDate();

    const meetingDate = meetingTimezone ? toZonedTime(utcDate, meetingTimezone) : null;
    const displayDate = meetingTimezone ? meetingDate : userDate;
    const monthDay = format(displayDate, 'MMM d');

    let timeStr = '';
    if (includeTime) {
      const dateForTime = meetingTimezone ? meetingDate : userDate;
      timeStr = dateForTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: meetingTimezone || undefined
      });
    }

    if (isToday) {
      const hour = userDate.getHours();
      const label = hour >= 17 ? 'Tonight' : 'Today';
      return includeTime ? `${label}, ${monthDay}, ${timeStr}` : `${label}, ${monthDay}`;
    }
    if (isTomorrow) {
      return includeTime ? `Tomorrow, ${monthDay}, ${timeStr}` : `Tomorrow, ${monthDay}`;
    }

    const dateForLabel = meetingTimezone ? (meetingDate || displayDate) : displayDate;
    const dayOfWeek = format(dateForLabel, 'EEE');
    return includeTime ? `${dayOfWeek}, ${monthDay}, ${timeStr}` : `${dayOfWeek}, ${monthDay}`;
  } catch (e) {
    try {
      const dateObj = convertUTCToMeetingTimezone(dateString, meetingTimezone);
      if (!dateObj) return includeTime ? '' : 'TBD';
      const dayOfWeek = format(dateObj, 'EEE');
      const monthDay = format(dateObj, 'MMM d');
      if (includeTime) {
        const timeStr = dateObj.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: meetingTimezone || undefined
        });
        return `${dayOfWeek}, ${monthDay}, ${timeStr}`;
      }
      return `${dayOfWeek}, ${monthDay}`;
    } catch (_) {
      return includeTime ? '' : 'TBD';
    }
  }
};

/**
 * Format a date in meeting timezone for display (short format: "Mon, Oct 15")
 * @param {Date|string} date - UTC date object or ISO string
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, timezone = null) => {
  if (!date) return '';
  const d = parseScheduledInstantForDisplay(date);
  if (!d) return '';
  const tz = resolveDisplayTimezoneIana(timezone);
  try {
    return formatInTimeZone(d, tz, 'MMM d');
  } catch {
    return '';
  }
};

/**
 * Format a date and time in meeting timezone for display
 * @param {Date|string} date - UTC date object or ISO string
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {Object} Object with formatted date and time strings
 */
export const formatDateTimeInTimezone = (date, timezone = null) => {
  if (!date) return { date: '', time: '' };
  const d = parseScheduledInstantForDisplay(date);
  if (!d) return { date: '', time: '' };
  const tz = resolveDisplayTimezoneIana(timezone);
  try {
    return {
      date: formatInTimeZone(d, tz, 'MMM d'),
      time: formatInTimeZone(d, tz, 'h:mm a'),
    };
  } catch {
    return { date: '', time: '' };
  }
};

/**
 * Parse a datetime string into separate date and time components for form display
 * Converts UTC time to meeting timezone for display
 * @param {string} scheduledDate - ISO datetime string in UTC
 * @param {string} meetingTimezone - IANA timezone string (optional)
 * @returns {Object} Object with date, hour, minute, and period
 */
export const parseDateTimeForForm = (scheduledDate, meetingTimezone = null) => {
  if (!scheduledDate) return { date: "", hour: "", minute: "", period: "" };
  
  // Convert UTC to meeting timezone if provided
  let date;
  if (meetingTimezone) {
    const utcString = scheduledDate instanceof Date ? scheduledDate.toISOString() : scheduledDate;
    date = convertUTCToMeetingTimezone(utcString, meetingTimezone);
    if (!date) {
      date = scheduledDate instanceof Date ? scheduledDate : new Date(scheduledDate);
    }
  } else {
    date = scheduledDate instanceof Date ? scheduledDate : new Date(scheduledDate);
  }
  
  // Get date components in the meeting timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD format
  
  const hour24 = date.getHours();
  const minute = date.getMinutes();
  
  // Convert to 12-hour format
  const { hour12, period } = convertTo12HourFormat(hour24);
  
  return {
    date: dateStr,
    hour: hour12.toString(),
    minute: minute.toString().padStart(2, '0'),
    period: period
  };
};

/**
 * Convert 24-hour format to 12-hour format
 * @param {number} hour24 - Hour in 24-hour format (0-23)
 * @returns {Object} Object with hour12 and period
 */
export const convertTo12HourFormat = (hour24) => {
  let hour12 = hour24;
  let period = "AM";
  
  if (hour24 === 0) {
    hour12 = 12;
  } else if (hour24 === 12) {
    hour12 = 12;
    period = "PM";
  } else if (hour24 > 12) {
    hour12 = hour24 - 12;
    period = "PM";
  }
  
  return { hour12, period };
};

/**
 * Convert 12-hour format to 24-hour format
 * @param {number} hour12 - Hour in 12-hour format (1-12)
 * @param {string} period - AM or PM
 * @returns {number} Hour in 24-hour format (0-23)
 */
export const convertTo24HourFormat = (hour12, period) => {
  let hour24 = hour12;
  
  if (period === "AM" && hour12 === 12) {
    hour24 = 0;
  } else if (period === "PM" && hour12 !== 12) {
    hour24 = hour12 + 12;
  }
  
  return hour24;
};

// ——— HTML `datetime-local` (browser local calendar fields) ———

function padDatetimeSegment(n) {
  return String(n).padStart(2, "0");
}

/**
 * Default value for datetime-local inputs: next 30-minute boundary in the browser's local timezone.
 * @returns {string} e.g. '2026-03-20T14:30'
 */
export function getDefaultScheduledDatetimeLocal() {
  const d = new Date();
  d.setMinutes(Math.ceil(d.getMinutes() / 30) * 30, 0, 0);
  return `${d.getFullYear()}-${padDatetimeSegment(d.getMonth() + 1)}-${padDatetimeSegment(d.getDate())}T${padDatetimeSegment(d.getHours())}:${padDatetimeSegment(d.getMinutes())}`;
}

/**
 * Datetime-local string from a Date or ISO/UTC string. Invalid or missing input falls back to {@link getDefaultScheduledDatetimeLocal}.
 * @param {string|Date|null|undefined} scheduledDate
 * @returns {string}
 */
export function scheduledDateToDatetimeLocalInput(scheduledDate) {
  if (!scheduledDate) return getDefaultScheduledDatetimeLocal();
  const d = parseScheduledInstantForDisplay(scheduledDate);
  if (!d) return getDefaultScheduledDatetimeLocal();
  return `${d.getFullYear()}-${padDatetimeSegment(d.getMonth() + 1)}-${padDatetimeSegment(d.getDate())}T${padDatetimeSegment(d.getHours())}:${padDatetimeSegment(d.getMinutes())}`;
}

/**
 * Same as scheduledDateToDatetimeLocalInput but formats in a specific timezone (for admin console when meeting timezone is set).
 * @param {string|Date|null|undefined} scheduledDate - UTC date
 * @param {string} timezone - IANA timezone (e.g. Europe/Paris)
 * @returns {string}
 */
export function scheduledDateToDatetimeLocalInTimezone(scheduledDate, timezone) {
  const tz = typeof timezone === 'string' ? timezone.trim() : '';
  if (!scheduledDate || !tz) return scheduledDateToDatetimeLocalInput(scheduledDate);
  try {
    const d = parseScheduledInstantForDisplay(scheduledDate);
    if (!d) return getDefaultScheduledDatetimeLocal();
    return formatInTimeZone(d, tz, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return scheduledDateToDatetimeLocalInput(scheduledDate);
  }
}

/**
 * Convert a datetime-local string (YYYY-MM-DDTHH:mm) to UTC ISO string, interpreting the value as local time in the given timezone.
 * Use when the admin selects a run timezone and the datetime-local represents that zone's time.
 * @param {string} datetimeLocalStr - e.g. "2026-03-21T18:00"
 * @param {string} timezone - IANA timezone (e.g. Europe/Paris)
 * @returns {string} ISO datetime string in UTC
 */
export function convertDatetimeLocalToUTCInTimezone(datetimeLocalStr, timezone) {
  if (!datetimeLocalStr || !timezone || typeof datetimeLocalStr !== "string") {
    throw new Error("datetimeLocalStr and timezone are required");
  }
  const [datePart, timePart] = datetimeLocalStr.split("T");
  if (!datePart || !timePart) throw new Error("Invalid datetime-local format");
  const [y, m, d] = datePart.split("-").map(Number);
  const [h, min] = timePart.split(":").map(Number);
  const localDate = new Date(y, m - 1, d, h, min, 0, 0);
  const utcDate = fromZonedTime(localDate, timezone);
  return utcDate.toISOString();
}
